/* Copyright (c) 2015 Google Inc. All rights reserved. */
/*
  This is a custom adaptation of the google-map-marker element
*/
/*
  FIXME(polymer-modulizer): the above comments were extracted
  from HTML and may be out of place here. Review them and
  then delete this comment!
*/
import '@polymer/polymer/polymer-legacy.js';

import '../google-apis/google-maps-api.js';
import { Polymer } from '@polymer/polymer/lib/legacy/polymer-fn.js';
import { html } from '@polymer/polymer/lib/utils/html-tag.js';
import '@katapult/js-rich-marker';
import { PickAnAttribute } from '../../modules/PickAnAttribute.js';

Polymer({
  _template: html`
    <style>
      :host {
        display: none;
      }
    </style>
    <slot></slot>
  `,

  is: 'google-map-rich-marker',

  /**
   * Fired when the marker icon was clicked. Requires the clickEvents attribute to be true.
   * @param {google.maps.MouseEvent} event The mouse event.
   * @event google-map-marker-click
   */

  /**
   * Fired when the marker icon was double clicked. Requires the clickEvents attribute to be true.
   * @param {google.maps.MouseEvent} event The mouse event.
   * @event google-map-marker-dblclick
   */

  /**
   * Fired repeatedly while the user drags the marker. Requires the dragEvents attribute to be true.
   * @event google-map-marker-drag
   */

  /**
   * Fired when the user stops dragging the marker. Requires the dragEvents attribute to be true.
   * @event google-map-marker-dragend
   */

  /**
   * Fired when the user starts dragging the marker. Requires the dragEvents attribute to be true.
   * @event google-map-marker-dragstart
   */

  /**
   * Fired for a mousedown on the marker. Requires the mouseEvents attribute to be true.
   * @event google-map-marker-mousedown
   * @param {google.maps.MouseEvent} event The mouse event.
   */

  /**
   * Fired when the DOM `mousemove` event is fired on the marker. Requires the mouseEvents
   * attribute to be true.
   * @event google-map-marker-mousemove
   * @param {google.maps.MouseEvent} event The mouse event.
   */

  /**
   * Fired when the mouse leaves the area of the marker icon. Requires the mouseEvents attribute to be
   * true.
   * @event google-map-marker-mouseout
   * @param {google.maps.MouseEvent} event The mouse event.
   */

  /**
   * Fired when the mouse enters the area of the marker icon. Requires the mouseEvents attribute to be
   * true.
   * @event google-map-marker-mouseover
   * @param {google.maps.MouseEvent} event The mouse event.
   */

  /**
   * Fired for a mouseup on the marker. Requires the mouseEvents attribute to be true.
   *
   * @event google-map-marker-mouseup
   * @param {google.maps.MouseEvent} event The mouse event.
   */

  /**
   * Fired for a rightclick on the marker. Requires the clickEvents attribute to be true.
   * @event google-map-marker-rightclick
   * @param {google.maps.MouseEvent} event The mouse event.
   */

  /**
   * Fired when an infowindow is opened.
   * @event google-map-marker-open
   */

  /**
   * Fired when the close button of the infowindow is pressed.
   * @event google-map-marker-close
   */

  properties: {
    /**
     * A Google Maps marker object.
     * @type google.maps.Marker
     */
    marker: {
      type: Object,
      notify: true
    },

    /**
     * The Google map object.
     * @type google.maps.Map
     */
    map: {
      type: Object,
      observer: '_mapChanged'
    },

    /**
     *  When true, google-map will ignore marker when it's fitToMarkers is changed
     */
    noFit: {
      type: Boolean,
      value: false,
      reflectToAttribute: true,
      observer: '_noFitChanged'
    },

    /**
     *  Attributes of underlying node to use in node labels
     */
    attributes: Object,

    /**
     *  Keys of attribtues to display on map in labels
     */
    attributesToUse: Object,

    /**
     *  When true, marker will show node label
     */
    showLabel: {
      type: Boolean,
      value: false
    },

    /**
     *  RichMarker label object
     */
    label: {
      type: Object,
      value: null
    },

    /**
     * A Google Map Infowindow object.
     * @type {?Object}
     */
    info: {
      type: Object,
      value: null
    },

    /**
     * When true, marker *click events are automatically registered.
     */
    clickEvents: {
      type: Boolean,
      value: false,
      observer: '_clickEventsChanged'
    },

    /**
     * When true, marker drag* events are automatically registered.
     */
    dragEvents: {
      type: Boolean,
      value: false,
      observer: '_dragEventsChanged'
    },

    richContent: {
      type: Object,
      value: null,
      observer: '_richContentChanged'
    },

    /**
     * When true, marker mouse* events are automatically registered.
     */
    mouseEvents: {
      type: Boolean,
      value: false,
      observer: '_mouseEventsChanged'
    },

    /**
     * Z-index for the marker icon.
     */
    zIndex: {
      type: Number,
      value: 0,
      observer: '_zIndexChanged'
    },

    /**
     * The marker's longitude coordinate.
     */
    longitude: {
      type: Number,
      value: null,
      notify: true
    },

    /**
     * The marker's latitude coordinate.
     */
    latitude: {
      type: Number,
      value: null,
      notify: true
    },

    /**
     * A animation for the marker. "DROP" or "BOUNCE". See
     * https://developers.google.com/maps/documentation/javascript/examples/marker-animations.
     */
    animation: {
      type: String,
      value: null,
      observer: '_animationChanged'
    },

    /**
     * Specifies whether the InfoWindow is open or not
     */
    open: {
      type: Boolean,
      value: false,
      observer: '_openChanged'
    },

    /**
     * Specify marker's cursor
     */
    cursor: {
      type: String,
      value: 'pointer',
      observer: '_cursorChanged'
    },

    /**
     *
     */
    clickable: {
      type: Boolean,
      value: false,
      observer: '_clickableChanged'
    },

    notOptimized: {
      type: Boolean,
      value: false,
      observer: '_notOptimizedChanged'
    }
  },

  observers: ['changeLabel(showLabel, attributes, attributesToUse, marker)', '_updatePosition(latitude, longitude)'],

  ready: function () {
    this._boundChangeLabel = this.changeLabel.bind(this);
    this._boundMapReady = this._mapReady.bind(this);
  },

  detached: function () {
    if (this.marker) {
      google.maps.event.clearInstanceListeners(this.marker);
      this._listeners = {};
      this.marker.setMap(null);
    }
    if (this.label) this.label.setMap(null);
    if (this._contentObserver) this._contentObserver.disconnect();

    window.removeEventListener('richmarker-ready', this._boundChangeLabel);
    window.removeEventListener('richmarker-ready', this._boundMapReady);
  },

  attached: function () {
    // If element is added back to DOM, put it back on the map.
    if (this.marker) {
      this.marker.setMap(this.map);
    }
  },

  changeLabel: function () {
    if (!this.showLabel) return null;

    // wait for richmarker if not yet defined
    if (!window.RichMarker) {
      if (!this._changeListener) {
        window.addEventListener('richmarker-ready', this._boundChangeLabel);
        this._changeListener = true;
      }
      return null;
    } else window.removeEventListener('richmarker-ready', this._boundChangeLabel);

    // create label if none exists
    if (!this.label) {
      let list = [],
        attr = this.attributes;

      // build list of node attributes that match nodeLabels
      for (let label in this.attributesToUse)
        if (attr && PickAnAttribute(attr, label)) for (let key in attr[label]) list.push({ label, value: attr[label][key] });

      // only create label if attributes exist on node
      let t = list.length || 0;
      if (t > 0) {
        let content = document.createElement('div'),
          position = this.marker.getPosition();
        content.className = 'mapLabel';

        for (let i = 0; i < t; i++) {
          let item = document.createElement('div');
          item.className = 'mapLabelItem';
          if (t > 1) item.textContent = list[i].label + ':  ';
          item.textContent += this._getAttributeLabel(list[i].label, list[i].value, '');
          content.appendChild(item);
        }

        this.label = new RichMarker({
          anchor: RichMarkerPosition.LEFT,
          content,
          flat: true,
          map: this.map,
          pane: 'markerLayer',
          position,
          zIndex: 6 // just over non-pole node_type markers, also over section markers
        });
      }
    } else {
      let list = [],
        attr = this.attributes;

      // build list of node attributes that match nodeLabels
      for (let label in this.attributesToUse)
        if (attr && PickAnAttribute(attr, label)) for (let key in attr[label]) list.push({ label, value: attr[label][key] });

      // if attributes exist on node, replace current labels with new ones
      let t = list.length || 0;
      if (t > 0) {
        let content = this.label.getContent();

        // remove current labels
        while (content.hasChildNodes()) content.removeChild(content.lastChild);

        // re-add node's labels
        for (let i = 0; i < t; i++) {
          let item = document.createElement('div');
          item.className = 'mapLabelItem';
          if (t > 1) item.textContent = list[i].label + ':  ';
          item.textContent += this._getAttributeLabel(list[i].label, list[i].value, '');
          content.appendChild(item);
        }

        // reset label anchor in case label height has changed (if anchored at TOP_LEFT, this might not be necessary)
        this.label.setAnchor(RichMarkerPosition.LEFT);
      } else {
        // if no more attributes, remove marker from map and marker
        this.label.setMap(null);
        this.label = null;
      }
    }
  },

  _getAttributeLabel: function (property, value, divider) {
    if (property == 'time_bucket') return this._formatTime(value.start) + '-' + this._formatTime(value.stop);
    else if (property == 'pole_tag') return (value.company || 'not set!') + ' - ' + value.tagtext;
    else if (property == 'sizes_of_attached_dn_guys') {
      let label = '',
        sizes = value.replace(/\s+/g, '').split(','),
        t = sizes.length || 0;
      for (var i = 0; i < t; i++) {
        label += sizes[i] + divider;
      }
      return label;
    } else return value;
  },

  _formatTime: function (time) {
    if (time == null) return '';
    var date = new Date(time);
    var hours = ('0' + date.getHours()).slice(-2);
    var minutes = ('0' + date.getMinutes()).slice(-2);
    var seconds = ('0' + date.getSeconds()).slice(-2);
    return hours + ':' + minutes + ':' + seconds;
  },

  _updatePosition: function () {
    if (this.marker && this.latitude != null && this.longitude != null) {
      this.marker.setPosition(new google.maps.LatLng(parseFloat(this.latitude), parseFloat(this.longitude)));
      if (this.label) this.label.setPosition(this.marker.getPosition());
    }
  },

  _clickEventsChanged: function () {
    if (this.map) {
      if (this.clickEvents) {
        this._forwardEvent('click');
        this._forwardEvent('dblclick');
        this._forwardEvent('rightclick');
      } else {
        this._clearListener('click');
        this._clearListener('dblclick');
        this._clearListener('rightclick');
      }
    }
  },

  _dragEventsChanged: function () {
    if (this.map) {
      if (this.dragEvents) {
        this._forwardEvent('drag');
        this._forwardEvent('dragend');
        this._forwardEvent('dragstart');
      } else {
        this._clearListener('drag');
        this._clearListener('dragend');
        this._clearListener('dragstart');
      }
    }
  },

  _mouseEventsChanged: function () {
    if (this.map) {
      if (this.mouseEvents) {
        this._forwardEvent('mousedown');
        this._forwardEvent('mousemove');
        this._forwardEvent('mouseout');
        this._forwardEvent('mouseover');
        this._forwardEvent('mouseup');
      } else {
        this._clearListener('mousedown');
        this._clearListener('mousemove');
        this._clearListener('mouseout');
        this._clearListener('mouseover');
        this._clearListener('mouseup');
      }
    }
  },

  _animationChanged: function () {
    if (this.marker) {
      this.marker.setAnimation(google.maps.Animation[this.animation]);
    }
  },

  _richContentChanged: function () {
    if (this.marker) {
      this.marker.setContent(this.richContent);
    }
  },

  _zIndexChanged: function () {
    if (this.marker) {
      this.marker.setZIndex(this.zIndex);
    }
  },

  _cursorChanged: function () {
    if (this.marker) {
      this.marker.setCursor(this.cursor);
    }
  },

  _clickableChanged: function () {
    if (this.marker) {
      this.marker.setClickable(this.clickable);
    }
  },

  _notOptimizedChanged: function () {
    if (this.marker) {
      this.marker.setOptions({ optimized: !this.notOptimized });
    }
  },

  _mapChanged: function () {
    // Marker will be rebuilt, so disconnect existing one from old map and listeners.
    if (this.marker) {
      this.marker.setMap(null);
      google.maps.event.clearInstanceListeners(this.marker);
    }

    if (this.map && this.map instanceof google.maps.Map) {
      this._mapReady();
    }
  },

  _noFitChanged: function () {
    if (this.marker) this.set('marker.noFit', this.noFit);
  },

  _contentChanged: function () {
    if (this._contentObserver) this._contentObserver.disconnect();
    // Watch for future updates.
    this._contentObserver = new MutationObserver(this._contentChanged.bind(this));
    this._contentObserver.observe(this, {
      childList: true,
      subtree: true
    });

    var content = this.innerHTML.trim();
    if (content) {
      if (!this.info) {
        // Create a new infowindow
        this.info = new google.maps.InfoWindow();
        this.openInfoHandler_ = google.maps.event.addListener(
          this.marker,
          'click',
          function () {
            this.open = true;
          }.bind(this)
        );

        this.closeInfoHandler_ = google.maps.event.addListener(
          this.info,
          'closeclick',
          function () {
            this.open = false;
          }.bind(this)
        );
      }
      this.info.setContent(content);
    } else {
      if (this.info) {
        // Destroy the existing infowindow.  It doesn't make sense to have an empty one.
        google.maps.event.removeListener(this.openInfoHandler_);
        google.maps.event.removeListener(this.closeInfoHandler_);
        this.info = null;
      }
    }
  },

  _openChanged: function () {
    if (this.info) {
      if (this.open) {
        this.info.open(this.map, this.marker);
        this.fire('google-map-marker-open');
      } else {
        this.info.close();
        this.fire('google-map-marker-close');
      }
    }
  },

  _mapReady: function () {
    // wait for richmarker if not yet defined
    if (!window.RichMarker) {
      if (!this._changeListener) {
        window.addEventListener('richmarker-ready', this._boundMapReady);
        this._changeListener = true;
      }
      return null;
    } else window.removeEventListener('richmarker-ready', this._boundMapReady);

    this._listeners = {};

    var markerPos = new google.maps.LatLng(parseFloat(this.latitude), parseFloat(this.longitude));
    this.marker = new RichMarker({
      flat: true,
      map: this.map,
      position: markerPos,
      noFit: this.noFit,
      animation: google.maps.Animation[this.animation],
      clickable: this.clickable,
      draggable: this.draggable,
      visible: !this.hidden,
      zIndex: this.zIndex,
      cursor: this.cursor,
      optimized: !this.notOptimized,
      content: this.richContent
    });

    this._contentChanged();
    this._clickEventsChanged();
    this._dragEventsChanged();
    this._mouseEventsChanged();
    this._openChanged();
  },

  _clearListener: function (name) {
    if (this._listeners[name]) {
      google.maps.event.removeListener(this._listeners[name]);
      this._listeners[name] = null;
    }
  },

  _forwardEvent: function (name) {
    this._listeners[name] = google.maps.event.addListener(
      this.marker,
      name,
      function (event) {
        this.fire('google-map-marker-' + name, event);
      }.bind(this)
    );
  },

  attributeChanged: function (attrName) {
    if (!this.marker) {
      return;
    }

    // Cannot use *Changed watchers for native properties.
    switch (attrName) {
      case 'hidden':
        this.marker.setVisible(!this.hidden);
        break;
      case 'draggable':
        this.marker.setDraggable(this.draggable);
        break;
    }
  }
});
